import { faRedo } from '@fortawesome/pro-regular-svg-icons'
import { StyledSelect } from 'components/common/Select'
import { useCollectiveDemoContext } from 'context/CollectiveDemoContext'
import { CollectiveProposalStatus } from 'graphql/generated'
import { useUpdateCollectiveMembership } from 'hooks/collectives/demo/useUpdateCollectiveMembership'
import React, { useCallback } from 'react'
import { components } from 'react-select'
import { ControlLevelTypes, IControlLevelTypes } from './common'
import { Button, Fade, Typography } from 'components/common'
import Hidden from 'components/common/Hidden'

const Option = props => {
  const option = props.data
  return (
    <components.Option {...props}>
      <Typography component="div">
        <Typography>{option.label}</Typography>
        <Typography color="lightGray">{option.info}</Typography>
      </Typography>
    </components.Option>
  )
}

const DemoControls: React.FC = () => {
  const {
    resetting,
    setResetting,
    updateDemoCache,
    collectiveDemo,
    collectiveProposals,
    collectiveMembers,
    setCollectiveMembers,
    setControlLevel,
    controlLevel,
    setDemoWalletLinking,
    setDemoWalletLinked,
    resetSteps,
    refetchDemo,
    goToStep,
    currentStep,
    totalStepsCount,
    newProposals,
    setNewProposals,
    setViewingProposal,
    setViewingMembers,
    setDemoActiveProposal,
    getProposalDataForId,
    setProposalDataForId,
    updateDemoProposals,
    possibleMembers,
    setPossibleMembers
  } = useCollectiveDemoContext()

  const updateCollectiveMembership = useUpdateCollectiveMembership()

  const resetCollectivesStates = useCallback(() => {
    if (!collectiveDemo) {
      return
    }

    const newCollectiveDemo = { ...collectiveDemo }

    newCollectiveDemo.membershipRequest = undefined
    newCollectiveDemo.totalMembershipRequests = 0

    // update each users membership tokens and votes
    const newMembers = possibleMembers.map(member => {
      const memberNew = { ...member }
      memberNew.membership.totalTokens = '0'
      memberNew.membership.percentOfTotalTokenSupply = 0
      memberNew.votes = {}

      return memberNew
    })
    setPossibleMembers(newMembers)

    const newCollectiveMembers = { ...collectiveMembers }

    newProposals.forEach(id => {
      const proposalData = getProposalDataForId(id)

      if (proposalData) {
        const newProposalsData = { ...proposalData }
        //cleanup votes history
        newProposalsData.votes.edges = proposalData.votes.edges.filter(
          edge => !edge.node.id.startsWith('demoVote')
        )

        setProposalDataForId(id, newProposalsData)

        // cleanup members data
        const executed =
          newProposalsData?.proposal?.status ===
          CollectiveProposalStatus.EXECUTED

        const addMemberCommand = newProposalsData.proposal?.commands.find(
          com => com.__typename === 'CollectiveProposalAddMemberCommand'
        ) as {
          __typename: 'CollectiveProposalAddMemberCommand'
          memberAddress: string
        }

        if (executed && addMemberCommand) {
          const memberAdding = possibleMembers.find(
            type => type.address === addMemberCommand.memberAddress
          )

          newCollectiveMembers.edges = newCollectiveMembers.edges.filter(
            edge =>
              edge.node.user.address !== memberAdding?.membership.user.address
          )

          // clenaup collective params
          newCollectiveDemo.totalMembers = newCollectiveDemo.totalMembers - 1
        }
      }
    })

    setCollectiveMembers(newCollectiveMembers)

    //cleanup lists
    newCollectiveDemo.proposals.edges =
      newCollectiveDemo.proposals.edges.filter(
        edge => !edge.node.id.startsWith('proposalForDemo')
      )

    const originalProposals = collectiveProposals.filter(
      proposal =>
        !proposal.proposal ||
        (proposal.proposal &&
          !proposal.proposal.id.startsWith('proposalForDemo'))
    )

    updateDemoProposals(originalProposals)

    newCollectiveDemo.totalProposals =
      newCollectiveDemo.totalProposals - newProposals.size

    //at last
    setNewProposals(new Set())
    updateDemoCache({ ...newCollectiveDemo })
  }, [
    collectiveDemo,
    collectiveMembers,
    collectiveProposals,
    getProposalDataForId,
    newProposals,
    possibleMembers,
    setCollectiveMembers,
    setNewProposals,
    setPossibleMembers,
    setProposalDataForId,
    updateDemoCache,
    updateDemoProposals
  ])

  const skipToEnd = () => {
    reset({
      skipControlChangeToTutorial: true,
      controlLevel: ControlLevelTypes[2]
    })

    goToStep(totalStepsCount - 1)

    setDemoWalletLinking('connecting')
    setDemoWalletLinked(true)
    possibleMembers[0].membership.approved = true
    possibleMembers[0].membership.privileged = false
  }

  const reset = useCallback(
    ({
      skipControlChangeToTutorial = false,
      controlLevel = ControlLevelTypes[0]
    }: {
      skipControlChangeToTutorial?: boolean
      controlLevel?: IControlLevelTypes
    } = {}) => {
      setResetting(true)
      setTimeout(() => {
        setDemoWalletLinking('init')
        setDemoWalletLinked(false)

        setViewingProposal(undefined)
        setViewingMembers(false)
        setDemoActiveProposal(undefined)

        resetCollectivesStates()

        setControlLevel(controlLevel)

        if (!skipControlChangeToTutorial) {
          // change all guests to false
          possibleMembers[0].membership.approved = false
          possibleMembers[0].membership.privileged = false

          resetSteps()
        }
        setResetting(false)
      }, 300)
    },
    [
      setResetting,
      setDemoWalletLinking,
      setDemoWalletLinked,
      setViewingProposal,
      setViewingMembers,
      setDemoActiveProposal,
      resetCollectivesStates,
      setControlLevel,
      possibleMembers,
      resetSteps
    ]
  )

  const setControlLevelFn = useCallback(
    data => {
      setControlLevel(data)

      switch (data) {
        case ControlLevelTypes[0]:
          updateCollectiveMembership(false, false)
          break
        case ControlLevelTypes[1]:
          updateCollectiveMembership(false, false)
          break
        case ControlLevelTypes[2]:
          updateCollectiveMembership(true, false)
          break
        case ControlLevelTypes[3]:
          updateCollectiveMembership(true, true)
          break
      }

      if (data === ControlLevelTypes[0]) {
        reset()
      }
    },
    [reset, setControlLevel, updateCollectiveMembership]
  )

  return (
    <div className="shadow-md rounded-xl bg-gray-50 border border-solid border-gray-200">
      <div className="flex flex-1 gap-2 justify-between w-full items-center py-3 px-4">
        <div className="flex flex-1 gap-2 w-full items-center lg:border-r-2">
          <Typography className="text-sm">
            You&lsquo;re in Demo Mode!
          </Typography>

          <Hidden xsDown>
            <Button
              label="Reload Tutorial"
              loading={resetting}
              color="gray"
              icon={faRedo}
              iconPlacement="before"
              onClick={() => {
                refetchDemo()
                reset()
              }}
              className="text-[0.8125rem] rounded-lg bg-gray-500 hover:bg-gray-700 focus:ring-transparent text-white px-2.5 py-1"
            />
          </Hidden>

          <Hidden xsDown>
            <Button
              label="Skip To End"
              color="gray"
              onClick={skipToEnd}
              className="text-[0.8125rem] rounded-lg bg-gray-500 hover:bg-gray-700 focus:ring-transparent text-white px-2.5 py-1"
            />
          </Hidden>

          <Fade in={currentStep === totalStepsCount - 1}>
            <div className="flex-1 max-w-[250px] pl-2">
              <StyledSelect
                isDisabled={currentStep != totalStepsCount - 1}
                value={controlLevel}
                onChange={data => {
                  setControlLevelFn(data ? data : undefined)
                }}
                menuPlacement={'auto'}
                options={ControlLevelTypes.slice(1)}
                placeholder={'See a View manually'}
                components={{ Option }}
              />
            </div>
          </Fade>
        </div>
      </div>
      <Hidden smUp>
        <div className="flex mx-4 mb-2 gap-2 sm:hidden">
          <Button
            size="sm"
            label="Reload Tutorial"
            loading={resetting}
            color="gray"
            icon={faRedo}
            iconPlacement="before"
            onClick={() => {
              refetchDemo()
              reset()
            }}
            className="text-[0.8125rem] rounded-lg bg-gray-500 hover:bg-gray-700 focus:ring-transparent text-white px-2.5 py-1"
          />

          <Button
            size="sm"
            label="Skip To End"
            color="gray"
            onClick={skipToEnd}
            className="text-[0.8125rem] rounded-lg bg-gray-500 hover:bg-gray-700 focus:ring-transparent text-white px-2.5 py-1"
          />
        </div>
      </Hidden>
    </div>
  )
}

export default DemoControls
